import assign from 'lodash/assign';
import uiRouter from '@uirouter/angularjs';
import ngTranslate from 'angular-translate';
import { IMPORTER_TYPES } from 'app/constants';

import { ProductServiceModule } from 'services/data/products.service';
import { OrderServiceModule } from 'services/data/orders.service';
import { CustomerServiceModule } from 'app/pages/customers/customers.service';
import { BulkImportServiceModule } from 'services/data/bulkImport.service';
import { CategoryServiceModule } from 'services/data/categories.service';
import { CSVReaderService } from 'services/utilities/csvReader.service';
import { SegmentServiceModule } from 'services/tracking/segment/segment.service';

import template from './bulkImportProgressModal.html';
import './bulkImportProgressModal.scss';

export const BulkImportProgressModal = angular
    .module('app.pages.products.bulkImportProgressModal', [
        'ngMaterial',
        uiRouter,
        ngTranslate,
        ProductServiceModule,
        OrderServiceModule,
        CustomerServiceModule,
        BulkImportServiceModule,
        CategoryServiceModule,
        CSVReaderService,
        SegmentServiceModule
    ]).component('bulkImportProgressModal', {
        template,
        bindings: {
            csvObj: '<',
            fileName: '@',
            featureLimitation: '<',
            importerType: '<'
        },
        controller: class BulkImportProgressModalController {
            constructor(
                $scope,
                $mdDialog,
                $mdToast,
                productService,
                orderService,
                customerService,
                bulkImportService,
                categoryService,
                $state,
                $timeout,
                $interval,
                $window,
                $location,
                $translate,
                segmentService
            ) {
                'ngInject';

                assign(this, {
                    gtmid: '',
                    IMPORT_STATUS_POLL_INTERVAL: 3000,
                    $scope,
                    $mdDialog,
                    $mdToast,
                    productService,
                    orderService,
                    customerService,
                    bulkImportService,
                    categoryService,
                    $state,
                    $timeout,
                    $interval,
                    $window,
                    $location,
                    $translate,
                    segmentService
                });
            }

            $onInit() {
                this.gtmid = `admin_${this.$state.current.name}_bulkImportProgressModal`;
                this.bulkUpload();
                this.trackPage();
            }

            bulkUpload() {
                if (this.csvObj && this.csvObj.contents) {
                    if (this.importerType === IMPORTER_TYPES.PRODUCT) {
                        this.productService.createImportJob()
                            .then((result) => {
                                this.productService.bulkUpload(result.url, this.csvObj.contents);
                                this.pollForBulkUploadStatus(result.importerJobId);
                            });
                    } else if (this.importerType === IMPORTER_TYPES.CUSTOMER) {
                        this.customerService.bulkUpload(this.csvObj).then(() => {
                            this.pollForCustomerBulkUploadStatus();
                        });
                    } else {
                        this.orderService.bulkUpload(this.csvObj).then((orderUploadResponse) => {
                            this.handleImportResponse(orderUploadResponse.contents);
                        });
                    }
                }
            }

            pollForBulkUploadStatus(importJobId) {
                this.activeInterval = this.$interval(() => {
                    this.getBulkUploadStatus(importJobId);
                }, this.IMPORT_STATUS_POLL_INTERVAL);
            }

            stopPolling() {
                if (angular.isDefined(this.activeInterval)) {
                    this.$interval.cancel(this.activeInterval);
                    this.activeInterval = undefined;
                }
            }

            pollForCustomerBulkUploadStatus() {
                this.activeInterval = this.$interval(() => {
                    this.getBulkUploadCustomerStatus();
                }, this.IMPORT_STATUS_POLL_INTERVAL);
            }


            getBulkUploadCustomerStatus() {
                this.customerService.getBulkUploadStatus()
                    .then((response) => {
                        if (response && response.status !== 'InProgress') {
                            this.stopPolling();
                            this.handleImportResponse(response.result, response.status);
                        }
                    });
            }

            getBulkUploadStatus(importJobId) {
                this.productService.getBulkUploadStatus(importJobId)
                    .then((response) => {
                        if (response && response.status !== 'InProgress') {
                            this.stopPolling();
                            this.handleProductImportResponse(response.result);
                        }
                    }, (err) => {
                        if (err && err.status !== 404) {
                            this.stopPolling();
                            this.handleProductImportResponse(err);
                        }
                    });
            }

            handleImportResponse(response, status) {
                const formattedErrors = response.errorKeys;
                const isErrorFree = !this.bulkImportService.hasErrors(response);
                if (status === 'None' || this.bulkImportService.isImportSuccessful(response)) {
                    this.$mdDialog.hide();
                    this.$state.reload();
                    this.importSuccess = true;
                    this.showSuccess(response.fileRows, response.itemsAdded);
                } else if (!isErrorFree) {
                    this.showErrors(formattedErrors);
                }
            }

            handleProductImportResponse(response) {
                const formattedErrors = this.setFormattedErrors(response);
                const isErrorFree = !this.bulkImportService.hasErrors(response);

                if (this.bulkImportService.isImportSuccessful(response)) {
                    this.importSuccess = true;

                    if (!isErrorFree) {
                        const refreshOnClose = true;
                        this.showErrors(formattedErrors, refreshOnClose);
                    }

                    this.$timeout(() => {
                        this.categoryService.getCategories(true);
                        if (isErrorFree) {
                            this.$mdDialog.hide();
                            this.$state.reload();
                        }
                        this.showSuccess(response.fileRows, response.importedProducts, response.orderedExtensions);
                    }, 700);
                } else if (!isErrorFree) {
                    this.showErrors(formattedErrors);
                }
            }

            setFormattedErrors(response) {
                this.formattedErrors = this.bulkImportService.getFormattedErrors(response.errorKeys);
                return this.formattedErrors;
            }

            showErrors(formattedErrors, refreshOnClose = false) {
                if (formattedErrors.length === 1) {
                    this.showErrorWithMessage(formattedErrors[0], refreshOnClose);
                } else if (formattedErrors.length > 1) {
                    this.showErrorList(formattedErrors, refreshOnClose);
                }
            }

            showSuccess(attempted, imported, extensions) {
                this.$translate('IMPORTER_MODULE.SUCCESS_IMPORTED',
                    { COUNT: imported, ATTEMPTED: attempted, IMPORTER_TYPE: this.importerType }).then(
                    (importMessage) => {
                        if (extensions && this.hasNewExtensions(extensions)) {
                            this.$translate('PRODUCT.SUCCESSEXTENSIONS', { COUNT: extensions.length }).then(
                                (extensionsMessage) => {
                                    this.showSuccessToast(`${importMessage} ${extensionsMessage}`);
                                }
                            );
                        } else {
                            this.showSuccessToast(importMessage);
                        }
                    }
                );
            }

            // ideally this would check against the extensions already installed in the store
            hasNewExtensions(extensions) {
                return extensions.length > 0;
            }

            showSuccessToast(message) {
                this.$mdToast.show(this.$mdToast.simple()
                    .textContent(message)
                    .position('bottom left')
                    .hideDelay(10000)
                );
            }

            showErrorWithMessage(error, doRefreshOnClose) {
                this.$mdDialog.show({
                    clickOutsideToClose: true,
                    template: `<bulk-import-modal
                                single-error="${error}"
                                importer-type="importerType"
                                refresh-on-close="refreshOnClose"
                                feature-limitation="featureLimitation">
                                </bulk-import-modal>`,
                    locals: {
                        featureLimitation: this.featureLimitation,
                        refreshOnClose: doRefreshOnClose,
                        importerType: this.importerType
                    },
                    controller: ($scope, featureLimitation, refreshOnClose, importerType) => {
                        'ngInject';

                        $scope.featureLimitation = featureLimitation;
                        $scope.refreshOnClose = refreshOnClose;
                        $scope.importerType = importerType;
                    }
                });
            }

            showErrorList(formattedErrors, doRefreshOnClose) {
                this.$mdDialog.show({
                    clickOutsideToClose: true,
                    template: `<bulk-import-modal
                                error-list="errorList"
                                refresh-on-close="refreshOnClose"
                                importer-type="importerType"
                                feature-limitation="featureLimitation">
                                </bulk-import-modal>`,
                    locals: {
                        featureLimitation: this.featureLimitation,
                        errorList: formattedErrors,
                        refreshOnClose: doRefreshOnClose,
                        importerType: this.importerType

                    },
                    controller: ($scope, featureLimitation, errorList, refreshOnClose, importerType) => {
                        'ngInject';

                        $scope.featureLimitation = featureLimitation;
                        $scope.errorList = errorList;
                        $scope.refreshOnClose = refreshOnClose;
                        $scope.importerType = importerType;
                    }
                });
            }

            trackPage() {
                this.segmentService.trackGtmEvent({
                    analyticsClickEvent: 'virtualPageView'
                },
                {
                    virtualPage: `${this.$location.path()}/bulkimport/progress`,
                    virtualTitle: 'Bulk Import Upload Progress'
                });
            }
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/pages/products/components/bulkImportProgressModal.component.js